import React from 'react'
import { Link, useLocation } from 'react-router-dom'
import { cn } from '@/lib/utils'
import { Users, Building2, LogOut, FileText, AlertTriangle, Receipt, User, Share2 } from 'lucide-react'
import { useAuthStore } from '@/lib/store'
import { Button } from '@/components/ui/button'
import { useApiQuery } from '@/hooks/use-api-query'
import { GetAuthenticatedTenantUser } from '@/queries/get-authenticated-tenant-user.query'
import { BalanceDisplay } from '@/components/BalanceDisplay'
import { RoutePath } from '@/utils/routes'
import { useGianoAccount } from '@/hooks/use-giano-account'
import { useLogout } from '@/hooks/use-logout'
import { GetAuthenticatedRootAdmin } from '@/queries/get-authenticated-root-admin.query'
import { Address } from './Address'
import { Key, CreditCard } from 'lucide-react'

interface NavItemProps {
  icon: React.ReactNode
  label: string
  href: string
  badge?: number
}

const NavItem = ({ icon, label, href, badge }: NavItemProps) => {
  const location = useLocation()
  const isActive = location.pathname === href || location.pathname.startsWith(`${href}/`)

  return (
    <Link to={href} className="block my-1">
      <div
        className={cn(
          'flex items-center gap-3 px-3 py-2 rounded-md transition-colors',
          isActive
            ? 'bg-primary text-primary-foreground'
            : 'hover:bg-secondary/50 text-muted-foreground hover:text-primary',
        )}
      >
        {React.cloneElement(icon as React.ReactElement, {
          className: cn('h-5 w-5', isActive ? 'text-primary-foreground' : 'text-muted-foreground'),
        })}
        <span className="font-medium flex-1">{label}</span>
        {badge !== undefined && (
          <span
            className={cn(
              'px-2 py-0.5 text-xs font-medium rounded-full',
              isActive ? 'bg-primary-foreground text-primary' : 'bg-primary/10 text-primary',
            )}
          >
            {badge}
          </span>
        )}
      </div>
    </Link>
  )
}

export function Sidebar() {
  const { user } = useAuthStore()
  const isRootAdmin = user?.type === 'root_admin'

  const { logout: handleLogout } = useLogout()

  const { data: tenantUser } = useApiQuery(GetAuthenticatedTenantUser(), {
    enabled: Boolean(user) && user?.type === 'tenant',
  })
  const { data: rootAdminUser } = useApiQuery(GetAuthenticatedRootAdmin(), {
    enabled: Boolean(user) && user?.type === 'root_admin',
  })
  const { address: userAddress } = useGianoAccount()
  const userName = user?.type === 'root_admin' ? rootAdminUser?.name : tenantUser?.name

  return (
    <aside className="w-60 bg-card text-card-foreground h-screen overflow-hidden border-r border-border flex flex-col">
      <div className="p-4 flex items-center justify-center border-b border-border">
        <img src="/portafino-logo.png" alt="Portafino" className="h-12" />
      </div>

      <BalanceDisplay />

      <div className="flex-1 overflow-y-auto">
        <div className="p-2 mt-2">
          <nav className="space-y-1">
            {isRootAdmin ? (
              <>
                <NavItem href={RoutePath.Organizations} icon={<Building2 />} label="Organizations" />
                <NavItem href={RoutePath.Users} icon={<Users />} label="Users" />
                <NavItem
                  href={RoutePath.SharedBridgeWebhook}
                  icon={<Share2 />}
                  label="Shared Bridge Webhook"
                />
              </>
            ) : (
              <>
                <NavItem href={RoutePath.AdminAgreements} icon={<FileText />} label="Agreements" />
                <NavItem href={RoutePath.AdminDisputes} icon={<AlertTriangle />} label="Disputes" />
                <NavItem href={RoutePath.AdminTransactions} icon={<Receipt />} label="Transactions" />
                <NavItem href={RoutePath.AdminUsers} icon={<Users />} label="Users" />
                <NavItem href={RoutePath.AdminApiKey} icon={<Key />} label="API Key" />
                <NavItem href={RoutePath.AdminPaymentProviders} icon={<CreditCard />} label="Payment Providers" />
              </>
            )}
          </nav>
        </div>
      </div>

      <div className="border-t border-border p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3 max-w-full">
            <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center">
              <User className="h-4 w-4 text-primary" />
            </div>
            <div className="min-w-0 max-w-full">
              {/* TODO: Better solve long names */}
              <div className="text-md font-medium truncate max-w-full">{userName}</div>
              {userAddress && <Address address={userAddress} length={4} />}
            </div>
          </div>
          <Button variant="ghost" size="icon" onClick={handleLogout}>
            <LogOut className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </aside>
  )
}
